<template>
    <div class="max-w-8xl mx-auto w-full space-y-4">
        <!-- Page Header -->
        <div class="flex flex-wrap items-center justify-between gap-3">
            <h1 class="text-xl font-semibold text-gray-900 dark:text-white">
                Majors Management
            </h1>
            <button @click="openModal" class="btn btn-primary">
                <Plus class="w-4 h-4 mr-1" />
                Add Major
            </button>
        </div>

        <!-- Search and Filters -->
        <div class="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
            <div class="space-y-3">
                <!-- Search -->
                <input
                    v-model="searchQuery"
                    @input="debouncedSearch"
                    type="text"
                    placeholder="Search by name or code..."
                    class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white"
                />

                <div class="flex flex-wrap gap-3">
                    <!-- Status Filter -->
                    <SearchableSelect
                        v-model="filters.is_active"
                        :options="statusOptions"
                        placeholder="All Status"
                        option-label="label"
                        option-value="value"
                        class="flex-1 min-w-[140px]"
                        @update:model-value="fetchMajors"
                    />

                    <button
                        @click="resetFilters()"
                        v-if="hasActiveFilters"
                        class="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 text-sm font-medium hover:bg-gray-100 dark:hover:bg-gray-700 transition"
                    >
                        <RotateCcw class="w-4 h-4" />
                        Reset Filters
                    </button>
                </div>
            </div>
        </div>

        <!-- Loading State -->
        <div
            v-if="majorStore.loading"
            class="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden"
        >
            <SkeletonLoader variant="table" :rows="6" :cols="5" />
        </div>

        <!-- Error State -->
        <div
            v-if="majorStore.error"
            class="bg-danger-50 dark:bg-danger-900/20 border border-danger-200 dark:border-danger-800 rounded-lg p-4 mb-6"
        >
            <div class="flex">
                <div class="flex-shrink-0">
                    <div class="h-5 w-5 text-danger-400">⚠️</div>
                </div>
                <div class="ml-3">
                    <h3
                        class="text-sm font-medium text-danger-800 dark:text-danger-200"
                    >
                        Error
                    </h3>
                    <p
                        class="mt-1 text-sm text-danger-700 dark:text-danger-300"
                    >
                        {{ majorStore.error }}
                    </p>
                    <button
                        @click="majorStore.clearError"
                        class="mt-2 text-sm text-danger-600 dark:text-danger-400 hover:text-danger-500"
                    >
                        Dismiss
                    </button>
                </div>
            </div>
        </div>

        <!-- Data Table -->
        <div
            v-if="!majorStore.loading && !majorStore.error"
            class="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden"
        >
            <div class="overflow-x-auto">
                <table
                    class="min-w-full divide-y divide-gray-200 dark:divide-gray-700"
                    style="width: 100%"
                >
                    <thead class="bg-gray-50 dark:bg-gray-700">
                        <tr>
                            <th
                                class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
                            >
                                Code
                            </th>
                            <th
                                class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
                            >
                                Name
                            </th>
                            <th
                                class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
                            >
                                Description
                            </th>
                            <th
                                class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
                            >
                                Status
                            </th>
                            <th
                                class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
                            >
                                Actions
                            </th>
                        </tr>
                    </thead>
                    <tbody
                        class="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700"
                    >
                        <tr
                            v-for="major in majorStore.majors"
                            :key="major.id"
                            class="hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                        >
                            <td class="px-6 py-4 whitespace-nowrap">
                                <span
                                    class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary-100 text-primary-800 dark:bg-primary-900 dark:text-primary-200"
                                >
                                    {{ major.code }}
                                </span>
                            </td>
                            <td
                                class="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white"
                            >
                                {{ major.name }}
                            </td>
                            <td
                                class="px-6 py-4 text-sm text-gray-500 dark:text-gray-400"
                            >
                                {{ major.description || "-" }}
                            </td>
                            <td class="px-6 py-4 whitespace-nowrap">
                                <span
                                    v-if="major.is_active"
                                    class="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-success-100 text-success-800 dark:bg-success-900 dark:text-success-200"
                                >
                                    Active
                                </span>
                                <span
                                    v-else
                                    class="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-danger-100 text-danger-800 dark:bg-danger-900 dark:text-danger-200"
                                >
                                    Inactive
                                </span>
                            </td>
                            <td
                                class="px-6 py-4 whitespace-nowrap text-sm font-medium"
                            >
                                <button
                                    @click="editMajor(major)"
                                    title="Edit Major"
                                    class="inline-flex items-center justify-center p-2 text-primary-600 dark:text-primary-400 hover:text-primary-900 dark:hover:text-primary-300 rounded-md hover:bg-primary-50 dark:hover:bg-primary-900/20 transition-colors mr-2"
                                >
                                    <Pencil class="w-4 h-4" />
                                </button>
                                <button
                                    @click="confirmDelete(major)"
                                    title="Delete Major"
                                    class="inline-flex items-center justify-center p-2 text-danger-600 dark:text-danger-400 hover:text-danger-900 dark:hover:text-danger-300 rounded-md hover:bg-danger-50 dark:hover:bg-danger-900/20 transition-colors"
                                >
                                    <Trash2 class="w-4 h-4" />
                                </button>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <!-- Empty State -->
            <EmptyState
                v-if="majorStore.majors.length === 0"
                :icon="BookOpen"
                title="No majors found"
                description="Get started by adding a new major."
            >
                <button @click="openModal" class="btn btn-primary">
                    <Plus class="w-4 h-4 mr-1" />
                    Add Major
                </button>
            </EmptyState>

            <!-- Pagination -->
            <BasePagination
                :pagination="majorStore.meta"
                :per-page="majorStore.perPage"
                @page-change="goToPage"
                @per-page-change="handlePerPageChange"
            />
        </div>

        <!-- Major Modal -->
        <MajorModal
            v-if="showModal"
            :show-modal="showModal"
            :major="selectedMajor"
            :is-editing="isEditing"
            @close="closeModal"
            @saved="handleSaved"
        />

        <!-- Delete Confirmation Modal -->
        <DeleteConfirmationModal
            v-if="showDeleteModal"
            :show="showDeleteModal"
            :title="deleteModalTitle"
            :message="deleteModalMessage"
            @confirm="handleDelete"
            @close="closeDeleteModal"
        />
    </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from "vue";
import { useMajorStore } from "@/stores/majorStore";
import MajorModal from "@/modules/majors/components/MajorModal.vue";
import DeleteConfirmationModal from "@/components/DeleteConfirmationModal.vue";
import SearchableSelect from "@/components/SearchableSelect.vue";
import BasePagination from "@/components/BasePagination.vue";
import SkeletonLoader from "@/components/SkeletonLoader.vue";
import EmptyState from "@/components/EmptyState.vue";
import NotificationService from "@/utils/notifications";
import type { Major } from "@/api/major";
import { Plus, RotateCcw, Pencil, Trash2, BookOpen } from "@lucide/vue";

const majorStore = useMajorStore();
// State
const searchQuery = ref("");
const filters = ref({
    is_active: null as boolean | null,
});
const showModal = ref(false);
const showDeleteModal = ref(false);
const selectedMajor = ref<Major | null>(null);
const isEditing = ref(false);
const majorToDelete = ref<Major | null>(null);

// Delete modal properties
const deleteModalTitle = ref("Confirm Delete");
const deleteModalMessage = ref(
    "Are you sure you want to delete this major? This action cannot be undone.",
);

// Computed
const hasActiveFilters = computed(() => {
    return (
        searchQuery.value.trim() !== "" ||
        filters.value.is_active !== null ||
        majorStore.page !== 1 ||
        majorStore.perPage !== 10
    );
});

// Options for Status filter
const statusOptions = [
    { value: null, label: "All Status" },
    { value: true, label: "Active" },
    { value: false, label: "Inactive" },
];

// Methods
const handlePerPageChange = (value: number) => {
    majorStore.perPage = value;
    majorStore.page = 1;
    fetchMajors();
};

const fetchMajors = async () => {
    const searchFilters = {
        ...filters.value,
        search: searchQuery.value,
    };
    await majorStore.fetchMajors(searchFilters);
};

const debouncedSearch = useDebounce(fetchMajors, 300);

const openModal = () => {
    selectedMajor.value = null;
    isEditing.value = false;
    showModal.value = true;
};

const editMajor = (major: Major) => {
    selectedMajor.value = major;
    isEditing.value = true;
    showModal.value = true;
};

const closeModal = () => {
    showModal.value = false;
    selectedMajor.value = null;
    isEditing.value = false;
};

const confirmDelete = (major: Major) => {
    majorToDelete.value = major;
    deleteModalTitle.value = "Delete Major";
    deleteModalMessage.value = `Are you sure you want to delete ${major.name}? This action cannot be undone.`;
    showDeleteModal.value = true;
};

const closeDeleteModal = () => {
    showDeleteModal.value = false;
    majorToDelete.value = null;
};

const handleSaved = () => {
    // Refresh the majors list to show new/updated major
    fetchMajors();
    showNotification(
        isEditing.value
            ? "Major updated successfully"
            : "Major created successfully",
        "success",
    );
    closeModal();
};

const handleDelete = async () => {
    try {
        if (majorToDelete.value) {
            await majorStore.deleteMajor(majorToDelete.value.id);
            showNotification("Major deleted successfully", "success");
            closeDeleteModal();
        }
    } catch (error) {
        showNotification("Failed to delete major", "error");
    }
};

const goToPage = (page: number) => {
    majorStore.setPage(page);
    fetchMajors();
};

const resetFilters = () => {
    searchQuery.value = "";
    filters.value = { is_active: null };
    majorStore.setPage(1);
    majorStore.setPerPage(10);
    fetchMajors();
};

const showNotification = (message: string, type: "success" | "error") => {
    if (type === "success") {
        NotificationService.success(message);
    } else {
        NotificationService.error(message);
    }
};

// Debounce utility
function useDebounce(fn: Function, delay: number) {
    let timeoutId: ReturnType<typeof setTimeout>;
    return (...args: any[]) => {
        clearTimeout(timeoutId);
        timeoutId = setTimeout(() => fn(...args), delay);
    };
}

// Lifecycle
onMounted(() => {
    fetchMajors();
});
</script>
